import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/queryKeys'
import { queryKeys as capacityPlannerQueryKeys } from '@/features/capacity-planner/queryKeys'
import {
  createPlatform,
  deletePlatform,
  disconnectPlatforms,
  fetchClientRefreshHistory,
  fetchPlatformConnections,
  initPlatformEdit,
  manualRefreshPlatform,
  platformTypeToId,
  triggerClientRefresh,
  updatePlatform,
  type FetchPlatformConnectionsParams,
} from './api'
import type { PlatformCreatePayload, PlatformEditPayload } from './types'
import { advicePrepTrialLimitRejection } from '@/features/adviceprep/trialLimit'
import { ApiRequestError } from '@/lib/httpClient'
import { defaultQueryRetry } from '@/lib/queryClient'

export function invalidatePlatformListQueries(
  queryClient: QueryClient,
  clientId: string
) {
  if (!clientId) return Promise.resolve()
  return queryClient.invalidateQueries({
    queryKey: queryKeys.platform.all,
    predicate: (query) => {
      const [, queryType, filters] = query.queryKey
      return queryType === 'list'
        && typeof filters === 'object'
        && filters != null
        && 'clientId' in filters
        && filters.clientId === clientId
    },
  })
}

export function invalidateCapacityPlannerSettingsQuery(queryClient: QueryClient, clientId: string) {
  if (!clientId) return Promise.resolve()
  return queryClient.invalidateQueries({ queryKey: capacityPlannerQueryKeys.settings(clientId) })
}

async function invalidatePlatformMutationQueries(
  queryClient: QueryClient,
  clientId: string,
  _filters: Omit<FetchPlatformConnectionsParams, 'clientId'> = {}
) {
  await Promise.all([
    invalidatePlatformListQueries(queryClient, clientId),
    queryClient.invalidateQueries({ queryKey: queryKeys.userGroups.createInit(clientId) }),
    invalidateCapacityPlannerSettingsQuery(queryClient, clientId),
  ])
}

export function usePlatformConnections(params: FetchPlatformConnectionsParams, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.platform.list({
      clientId: params.clientId,
      name: params.name,
      companyName: params.companyName,
      type: params.type,
      status: params.status,
    }),
    queryFn: () => fetchPlatformConnections(params),
    enabled: !!params.clientId && (options.enabled ?? true),
  })
}

export function useInitPlatformEditMutation() {
  return useMutation({
    mutationFn: (platformId: string) => initPlatformEdit(platformId),
  })
}

export function useCreatePlatformMutation(
  clientId: string,
  filters: Omit<FetchPlatformConnectionsParams, 'clientId'> = {}
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: PlatformCreatePayload) => createPlatform({ ...payload, clientId }),
    onSuccess: async () => {
      await invalidatePlatformMutationQueries(queryClient, clientId, filters)
    },
    // A trial-limit 409 means the cached connection count was behind the
    // server's, which is why the button let the attempt through. Refetching the
    // list is what disables it and shows the explanation.
    onError: async (error) => {
      if (!advicePrepTrialLimitRejection(error)) return
      await invalidatePlatformListQueries(queryClient, clientId)
    },
  })
}

export function useUpdatePlatformMutation(
  clientId: string,
  filters: Omit<FetchPlatformConnectionsParams, 'clientId'> = {}
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: PlatformEditPayload) => updatePlatform({ ...payload, clientId }),
    onSuccess: async () => {
      await invalidatePlatformMutationQueries(queryClient, clientId, filters)
    },
    // Turning supportAdvisePrep on puts a connection on the meter, so this
    // endpoint enforces the trial allowance too. Same stale-count correction
    // as the create path.
    onError: async (error) => {
      if (!advicePrepTrialLimitRejection(error)) return
      await invalidatePlatformListQueries(queryClient, clientId)
    },
  })
}

export function useDisconnectPlatformsMutation(
  clientId: string,
  filters: Omit<FetchPlatformConnectionsParams, 'clientId'> = {}
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { platformIds: string[]; isBulk: boolean }) =>
      disconnectPlatforms(input.platformIds, input.isBulk),
    onSuccess: async () => {
      await invalidatePlatformMutationQueries(queryClient, clientId, filters)
    },
  })
}

export function useDeletePlatformMutation(
  clientId: string,
  filters: Omit<FetchPlatformConnectionsParams, 'clientId'> = {}
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (platformId: string) => deletePlatform(platformId),
    onSuccess: async () => {
      await invalidatePlatformMutationQueries(queryClient, clientId, filters)
    },
  })
}

export function useClientRefreshHistory(clientId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.platform.clientRefreshHistory(clientId),
    queryFn: () => fetchClientRefreshHistory(clientId),
    enabled: !!clientId && (options.enabled ?? true),
    staleTime: 0,
    retry: (failureCount, error) => {
      if (error instanceof ApiRequestError && (error.status === 400 || error.status === 403)) return false
      return defaultQueryRetry(failureCount, error)
    },
  })
}

export function useTriggerClientRefreshMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => triggerClientRefresh(clientId),
    // Refetched on failure too: after a 409, the running row is what disables Start.
    // Not awaited, or mutateAsync would hold the success toast until the refetch settles.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.platform.clientRefreshHistory(clientId) })
    },
  })
}

export function useManualRefreshMutation(
  clientId: string,
  _filters: Omit<FetchPlatformConnectionsParams, 'clientId'> = {}
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { platformId: string; type: PlatformEditPayload['type'] }) =>
      manualRefreshPlatform({
        platformId: input.platformId,
        typeId: platformTypeToId(input.type),
      }),
    onSuccess: async () => {
      await invalidatePlatformListQueries(queryClient, clientId)
    },
  })
}
